import React from "react";
import type { FloorPlanName } from "@/app/floor-plans/types";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useDotButton } from "@/components/ui/carousel-dot-button";
import { cls, cn } from "@/lib/utils";
import { useFloorPlanGallery } from "../../providers/FloorPlanGalleryProvider";
import { Skeleton } from "../skeleton";
import GalleryItem from "./floor-plan-gallery-item";

const NUMBER_LOADING_SECTIONS = 3;

interface FloorPlanGalleryProps {
  floorPlanName: FloorPlanName;
  onSetApi?: ((api: CarouselApi) => void) | null;
}

function FloorPlanGallery({
  floorPlanName,
  onSetApi = null,
}: FloorPlanGalleryProps) {
  const [galleryIsLoading, setGalleryIsLoading] = React.useState(true);
  const [api, setApi] = React.useState<CarouselApi>();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const { floorPlans, isLoading } = useFloorPlanGallery();

  const showFloorPlans = !isLoading && floorPlans !== null;

  const handleOnSetApi = React.useCallback(
    (value: CarouselApi) => {
      if (onSetApi) {
        onSetApi(value);
      }

      setApi(value);
    },
    [onSetApi],
  );

  const handleOnLoad = React.useCallback(() => {
    setGalleryIsLoading(false);
  }, []);

  return (
    <>
      <Carousel
        setApi={handleOnSetApi}
        opts={{ loop: true }}
        className={cls`w-full`}
      >
        <CarouselContent>
          {showFloorPlans &&
            floorPlans[floorPlanName].map(url => (
              <GalleryItem key={url} url={url} onLoad={handleOnLoad} />
            ))}

          {galleryIsLoading &&
            Array.from({ length: NUMBER_LOADING_SECTIONS }).map((_, index) => (
              <CarouselItem
                key={`${index + 2}`}
                className={cls`pl-5 basis-full md:basis-4/5 aspect-4/3`}
              >
                <Skeleton className="w-full h-full z-100 bg-primary" />
              </CarouselItem>
            ))}
        </CarouselContent>
      </Carousel>

      {showFloorPlans && (
        <div
          className={cls`
          flex flex-row
          justify-center items-center gap-2
          w-full h-fit
        `}
        >
          {scrollSnaps.map((_, index) => (
            <Button
              aria-label={`Go to slide ${(index + 1).toString()}`}
              key={`dot-${(index + 1).toString()}`}
              onClick={() => {
                onDotButtonClick(index);
              }}
              className={cn(
                `rounded-full bg-transparent w-3 h-3 p-0`,
                index !== selectedIndex && `bg-primary hover:bg-primary/50`,
              )}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default FloorPlanGallery;
